import { ChangeEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAxios } from '../../Helpers/AxiosHelper';
import { useNavigate } from 'react-router-dom';
import { Button, Chips, ChipsAddEvent, ChipsRemoveEvent, InputText, Editor, SCIcon, ProgressSpinner } from '../../Common/Imports';
import { ApiResponse, ReservationCommunications_EXT } from '../../Models/Classes';
import { ExtendedMessage, MailContext } from './Context';
import { ComunicationStatus } from '../../Models/Enums';
import { useEditorServerSync } from '../../Common/Hooks/useEditorServerSync';
interface DDPropertyContactsProps {
    IdReservation?: number;
    Type?: number;
    PopupMode?: boolean;
}

const MailNew: React.FC<DDPropertyContactsProps> = ({ IdReservation, Type, PopupMode = false }) => {
    const { onSend } = useContext(MailContext);
    const fileInputRef = useRef<HTMLInputElement | null>(null); // Reference for the file input

    const navigate = useNavigate();
    const [newMail, setNewMail] = useState<ExtendedMessage>({});
    const [To, setTo] = useState<string[]>([]);
    const [CC, setCC] = useState<string[]>([]);
    const [LoaderDownloadHistory, setLoaderDownloadHistory] = useState<boolean>(false);
    const [ConversationHistoryEnabled, setConversationHistoryEnabled] = useState<boolean>(false);
    const [isSending, setIsSending] = useState<boolean>(false);
    const [isTemplateLoading, setIsTemplateLoading] = useState<boolean>(Boolean(IdReservation));
    const AxiosHelper = useAxios();
    const _NewMailTemplate = useMemo<ExtendedMessage>(() => ({
        from: { emailAddress: { address: "reservationcheckhv@stayconfirmed.com" } },
        toRecipients: [],
        body: { content: '' },
        subject: ''
    }), []);

    // Ensure body content is always a string for the Editor
    const editorValue = newMail.body?.content ?? '';

    const debug = import.meta.env.DEV;

    const { editorRef, onLoad: onEditorLoad, reset: resetEditorSync, markApplied: markEditorUserEdited } = useEditorServerSync(editorValue);

    // Reset when changing context so we can apply the new server template once.
    useEffect(() => {
        resetEditorSync();
    }, [IdReservation, Type, resetEditorSync]);

    const GetData = useCallback(async () => {
        setIsTemplateLoading(true);
        try {
            const resp = await AxiosHelper.get<ApiResponse<ExtendedMessage>>(`Operative/GetEmailTemplate/${IdReservation}/${Type}`);
            if (resp.data.code === 0) {
                const responseData = resp.data.data as ExtendedMessage & {
                    Body?: { Content?: string; content?: string };
                    body?: { Content?: string; content?: string };
                };
                const bodyContent =
                    responseData.body?.content ??
                    responseData.body?.Content ??
                    responseData.Body?.content ??
                    responseData.Body?.Content ??
                    '';
                const normalizedData: ExtendedMessage = {
                    ...responseData,
                    body: {
                        ...(responseData.body ?? responseData.Body ?? {}),
                        content: bodyContent
                    }
                };

                resetEditorSync();
                setNewMail(normalizedData);
                const _T = normalizedData.toRecipients ?
                    normalizedData.toRecipients.map(recipient => recipient.emailAddress?.address || '') : [];

                setTo(_T);

                const _C = normalizedData.ccRecipients ?
                    normalizedData.ccRecipients.map(recipient => recipient.emailAddress?.address || '') : [];

                setCC(_C);
            }
            const resp2 = await AxiosHelper.get<ApiResponse<ReservationCommunications_EXT[]>>(`Operative/GetReservationComunications/${IdReservation}`);
            if (resp2.data.code === 0) {
                const _T = resp2.data.data.filter(m => m.status != ComunicationStatus.Scheduled).length > 0;
                setConversationHistoryEnabled(_T);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setIsTemplateLoading(false);
        }
    }, [AxiosHelper, IdReservation, Type, resetEditorSync]);

    useEffect(() => {
        if (IdReservation) {
            GetData()
        } else {
            setNewMail(_NewMailTemplate)
            setIsTemplateLoading(false);
        }
    }, [GetData, IdReservation, _NewMailTemplate]);

    const goBack = () => {
        navigate(-1); // Navigates back to the previous page
    };

    const AddTo = (e: ChipsAddEvent) => {
        if (To && !To.includes(e.value)) {
            const _T = [...To, e.value];
            setTo(_T);
        } else {
            return false;
        }
    };

    const AddCC = (e: ChipsAddEvent) => {
        if (CC && !CC.includes(e.value)) {
            const _T = [...CC, e.value];
            setCC(_T);
        } else {
            return false;
        }
    }

    const RemoveTo = (e: ChipsRemoveEvent) => {
        if (To) {
            const _T = To.filter(item => item !== e.value);
            setTo(_T);
        }
    }

    const RemoveCC = (e: ChipsRemoveEvent) => {
        if (CC) {
            const _T = CC.filter(item => item !== e.value);
            setCC(_T);
        }
    }

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;

        if (files) {
            setNewMail((prevState) => ({
                ...prevState,
                Files: files
            }));
        }
    };

    const sendMail = async () => {
        if (isSending) {
            return;
        }

        setIsSending(true);
        try {
            const mailToSend: ExtendedMessage = {
                ...newMail,
                toRecipients: To.map(m => ({ emailAddress: { address: m } })),
                ccRecipients: CC.map(m => ({ emailAddress: { address: m } }))
            };

            const sent = IdReservation
                ? await onSend(mailToSend, "IdReservation=" + IdReservation)
                : await onSend(mailToSend);

            if (!sent) {
                return;
            }

            GetData();
            setNewMail(prevState => ({ ...prevState, Files: null }));
            if (fileInputRef.current) {
                fileInputRef.current.value = ""; // Clears the file input
            }
        } catch (error) {
            console.error("Error sending mail:", error);
        } finally {
            setIsSending(false);
        }
    };

    const GetHistory = async () => {
        setLoaderDownloadHistory(true);
        try {
            // Make Axios request and specify responseType as 'blob' to handle binary data
            const resp = await AxiosHelper.get(`Operative/GetConversationHystory/reservationcheckhv@stayconfirmed.com/${IdReservation}`, {
                responseType: 'blob'  // Important for downloading files
            });

            // Create a URL for the file blob and trigger a download
            const url = window.URL.createObjectURL(new Blob([resp.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `ConversationHistory_${IdReservation}.pdf`); // Set file name
            document.body.appendChild(link);
            link.click();

            link?.parentNode?.removeChild(link);
        } catch (error) {
            console.error('Error fetching conversation history:', error);
        } finally {
            setLoaderDownloadHistory(false);
        }
    };

    return (
        <>
            <div className="row" style={{ maxWidth: PopupMode ? "50vw" : "100%" }}>

                {
                    !PopupMode &&
                    <div className="flex align-items-center mb-2">
                        <Button
                            type="button"
                            icon="pi pi-chevron-left"
                            severity="secondary"
                            className="surface-border text-900 w-3rem h-3rem mr-3"
                            outlined
                            onClick={goBack}
                        />
                        <span className="block text-900 font-bold text-xl">Compose Message</span>
                    </div>
                }

                <div className="row">
                    <div className="col-md-12 p-fluid">
                        <label htmlFor="username">To</label>
                        <Chips value={To} onAdd={AddTo} onRemove={RemoveTo} className="w-100" separator=";" />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-12 p-fluid">
                        <label htmlFor="username">CC</label>
                        <Chips value={CC} onAdd={AddCC} onRemove={RemoveCC} className="w-100" separator=";" />
                    </div>
                </div>
                <div className="row">
                    <div className="col-12 p-fluid">
                        <label htmlFor="Subject">
                            Subject
                        </label>
                        <InputText
                            id="subject"
                            value={newMail.subject ?? ''}
                            onChange={(e) =>
                                setNewMail((prevState) => ({
                                    ...prevState,
                                    subject: e.target.value
                                }))
                            }
                            placeholder="Subject"
                            className="w-100"
                            style={{ height: '3.5rem' }}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-11 p-fluid">
                        <input
                            type="file"
                            className="form-control"
                            multiple
                            onChange={handleFileChange}
                            ref={fileInputRef} />
                    </div>
                    {
                        ConversationHistoryEnabled &&
                        <div className="col-md-1 p-fluid d-flex align-items-center justify-content-center">
                            {
                                LoaderDownloadHistory ?
                                    <ProgressSpinner style={{ width: '25px', height: '25px' }} strokeWidth="2" fill="var(--surface-ground)" animationDuration=".5s" />
                                    :
                                    <SCIcon Name="Download" OnClick={GetHistory} Pointer />
                            }
                        </div>
                    }
                </div>
                <div className="row">
                    <div className="col-12  p-fluid">
                        {isTemplateLoading
                            ? <div className="flex justify-content-center align-items-center" style={{ height: '250px' }}>
                                <ProgressSpinner />
                            </div>
                            : <Editor
                                ref={editorRef}
                                style={{ height: '250px' }}
                                onLoad={(quill) => {
                                    if (debug) console.log('[MailNew] editor onLoad', { ok: !!quill });
                                    onEditorLoad(quill);
                                }}
                                onTextChange={(e) => {
                                    const html = e.htmlValue ?? '';
                                    if (debug) console.log('[MailNew] editor onTextChange', { len: html.length, source: e.source });

                                    if (e.source === 'user') {
                                        markEditorUserEdited();
                                    }

                                    setNewMail((prevState) => ({
                                        ...prevState,
                                        body: { ...(prevState.body ?? {}), content: html }
                                    }));
                                }}
                            />
                        }
                    </div>
                    <div className="col-12 flex column-gap-3 justify-content-end">
                        <Button
                            type="button"
                            className="h-3rem w-full sm:w-auto"
                            icon={isSending ? undefined : "pi pi-send"}
                            label={isSending ? "Sending..." : "Send Message"}
                            loading={isSending}
                            disabled={isSending}
                            onClick={sendMail}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default MailNew;
